import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { type EditorNextProps } from '@atlaskit/editor-core/composable-editor';
import { type useEditorPreset } from '@atlassian/conversation-assistant-content-renderer/controllers/editor';

export type EditorApi = ReturnType<typeof useEditorPreset>['editorApi'];

export type EditorSnapshot = {
	editorApi: EditorApi;
	editorProps: EditorNextProps;
};

export type EditorStore = {
	getSnapshot: () => EditorSnapshot;
	subscribe: (listener: () => void) => () => void;
	setSnapshot: (value: EditorSnapshot) => void;
};

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const createEditorStore = (initialValue: EditorSnapshot): EditorStore => {
	let currentValue = initialValue;
	const listeners = new Set<() => void>();

	return {
		getSnapshot: () => currentValue,
		subscribe: (listener) => {
			listeners.add(listener);
			return () => listeners.delete(listener);
		},
		setSnapshot: (value) => {
			if (Object.is(currentValue, value)) {
				return;
			}

			currentValue = value;
			listeners.forEach((listener) => listener());
		},
	};
};

export const useEditorStore = (editorApi: EditorApi, editorProps: EditorNextProps): EditorStore => {
	const value = useMemo(
		() => ({
			editorApi,
			editorProps,
		}),
		[editorApi, editorProps],
	);
	const storeRef = useRef<EditorStore | null>(null);
	if (!storeRef.current) {
		storeRef.current = createEditorStore(value);
	}

	useIsomorphicLayoutEffect(() => {
		storeRef.current?.setSnapshot(value);
	}, [value]);

	return storeRef.current;
};
